import create from 'zustand';
import { generateURLQuery } from '../react/utils/generate-url-query';
import { updateURLInAddressBar } from '../react/utils/update-url-in-address-bar';

// eslint-disable-next-line no-shadow
export enum CssMode {
  None = 1 << 0,
  CLOSE_IMMEDIATELY = 1 << 1,
  CLOSE_SLOWLY = 1 << 2,
  OPEN_SLOWLY = 1 << 3,
  OPEN_IMMEDIATELY = 1 << 4,
}

export const CssModeToCssStyle = (mode: CssMode) => {
  switch (mode) {
    case CssMode.CLOSE_IMMEDIATELY:
      return 'closeImmediately';
    case CssMode.CLOSE_SLOWLY:
      return 'closeSlowly';
    case CssMode.OPEN_SLOWLY:
      return 'openSlowly';
    case CssMode.OPEN_IMMEDIATELY:
      return 'openImmediately';
    default:
      return '';
  }
};

export const useStore = create<State>((set) => ({
  // Dialogs
  videoUri: '', // must be empty on start
  openVideoDialog: (videoUri: string) => set((/* state */) => ({ videoUri })),
  closeVideoDialog: () => set((/* state */) => ({ videoUri: '' })),

  menuOccupiesEntireSpace: false,
  expandMenu: () => set((/* state */) => ({ menuOccupiesEntireSpace: true })),
  shrinkMenu: () => set((/* state */) => ({ menuOccupiesEntireSpace: false })),

  query: {},
  setUrlQuery: (query: URL.Query) => set((/* state */) => ({ query })),

  uiHidden: false,
  setUiHidden: (uiHidden: boolean) => set((/* state */) => ({ uiHidden })),

  init: {
    isoCodes: [],
    startIso: '',
    fallbackIso: '',
    localisation: {},
    markdown: {},
  },
  setAvailableLocalisationData: (init: UI.Data) =>
    set((/* state */) => ({ init })),

  activeLanguage: {
    iso: '',
    file: '',
    fileContent: '',
    dict: {},
    pageRefs: {},
    tocTitles: [],
    tocSerialIds: [],
  },
  setActiveLanguage: (activeLanguage: Localisation.Data) => {
    set((/* state */) => ({ activeLanguage }));
    const query = generateURLQuery(activeLanguage.iso);
    const url = `${window.location.href.split('?')[0]}?${query}`;
    updateURLInAddressBar('', url);
  },

  // Menus
  infoWindowCssState: CssMode.OPEN_IMMEDIATELY,
  setInfoWindowStyle: (infoWindowCssState: CssMode) =>
    set((/* state */) => ({ infoWindowCssState })),
  settingsWindowCssState: CssMode.None,
  setSettingsWindowStyle: (settingsWindowCssState: CssMode) =>
    set((/* state */) => ({ settingsWindowCssState })),
  helpWindowCssState: CssMode.None,
  setHelpWindowStyle: (helpWindowCssState: CssMode) =>
    set((/* state */) => ({ helpWindowCssState })),
  langWindowCssState: CssMode.None,
  setLangWindowStyle: (langWindowCssState: CssMode) =>
    set((/* state */) => ({ langWindowCssState })),

  // On/Off Buttons - top
  helpBttnActive: false,
  openHelpMenu: () => set((/* state */) => ({ helpBttnActive: true })),
  closeHelpMenu: () => set((/* state */) => ({ helpBttnActive: false })),
  settingsBttnActive: false,
  openSettingsMenu: () => set((/* state */) => ({ settingsBttnActive: true })),
  closeSettingsMenu: () =>
    set((/* state */) => ({ settingsBttnActive: false })),
  infoBttnActive: true,
  openInfoMenu: () => set((/* state */) => ({ infoBttnActive: true })),
  closeInfoMenu: () => set((/* state */) => ({ infoBttnActive: false })),
  langBttnActive: false,
  openLanguageMenu: () => set((/* state */) => ({ langBttnActive: true })),
  closeLanguageMenu: () => set((/* state */) => ({ langBttnActive: false })),
  moreBttnActive: false,
  openSidebarInPortraitMode: () =>
    set((/* state */) => ({ moreBttnActive: true })),
  closeSidebarInPortaitMode: () =>
    set((/* state */) => ({ moreBttnActive: false })),

  // On/Off Buttons - center
  powerBttnActive: false,
  turnPowerOn: () => set((/* state */) => ({ powerBttnActive: true })),
  turnPowerOff: () => set((/* state */) => ({ powerBttnActive: false })),
  hotspotBttnActive: true,
  showHotspots: () => set((/* state */) => ({ hotspotBttnActive: true })),
  hideHotspots: () => set((/* state */) => ({ hotspotBttnActive: false })),
  cameraBttnActive: true,
  pinCamera: () => set((/* state */) => ({ cameraBttnActive: true })),
  freeCamera: () => set((/* state */) => ({ cameraBttnActive: false })),
  govieModeBttnActive: true,
  selectContinuousMode: () =>
    set((/* state */) => ({ govieModeBttnActive: true })),
  selectSingleStepMode: () =>
    set((/* state */) => ({ govieModeBttnActive: false })),

  // On/Off Buttons - bottom
  playbarBttnActive: true,
  showPlaybar: () => set((/* state */) => ({ playbarBttnActive: true })),
  hidePlaybar: () => set((/* state */) => ({ playbarBttnActive: false })),

  // Infobox
  pageId: '0',
  setPageId: (pageId: string) => set((/* state */) => ({ pageId })),
  pageIndex: 0,
  setActivePageIndex: (pageIndex: number) =>
    set((/* state */) => ({ pageIndex })),
  lastPageIndex: 0,
  setLastPageIndex: (lastPageIndex: number) =>
    set((/* state */) => ({ lastPageIndex })),

  // Settings
  mouseRotation: 50,
  setMouseRotation: (mouseRotation: number) =>
    set((/* state */) => ({ mouseRotation })),
  mouseZoom: 50,
  setMouseZoom: (mouseZoom: number) => set((/* state */) => ({ mouseZoom })),
  touchRotation: 50,
  setTouchRotation: (touchRotation: number) =>
    set((/* state */) => ({ touchRotation })),
  touchZoom: 50,
  setTouchZoom: (touchZoom: number) => set((/* state */) => ({ touchZoom })),

  // Playbar
  tocOpen: false,
  openToc: () => set((/* state */) => ({ tocOpen: true })),
  closeToc: () => set((/* state */) => ({ tocOpen: false })),
  playPause: false,
  showPauseButton: () => set((/* state */) => ({ playPause: true })),
  showPlayButton: () => set((/* state */) => ({ playPause: false })),

  windingIcon: 'none',
  showDefaultIcon: () => set((/* state */) => ({ windingIcon: 'none' })),
  showWindForwardIcon: () => set((/* state */) => ({ windingIcon: 'forward' })),
  showWindBackwardIcon: () =>
    set((/* state */) => ({ windingIcon: 'backward' })),
}));

export const staticSettings = {
  sidebar: {
    renderBttnsTop: true,
    renderBttnsCenter: true,
    renderBttnsBottom: true,
    renderUspBttn: false,
    renderContactBttn: true,
    renderDocsBttn: true,
    renderPowerBttn: false,
    renderHotspotBttn: false,
    renderGovieBttn: true,
    renderCameraBttn: true,
    renderStopBttn: false,
  },
  settings: {
    mouseRotationMin: 1,
    mouseRotationMax: 100,
    mouseZoomMin: 1,
    mouseZoomMax: 100,
    touchRotationMin: 1,
    touchRotationMax: 100,
    touchZoomMin: 1,
    touchZoomMax: 100,
  },
};
